import React, { useState } from "react";
import axios from 'axios';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getSettings } from "../../Helpers/SettingsHelper";
import { Paths } from "../../Common/Paths";
import { Button, Card, PasswordField, ProgressBar, SubmitHandler, Path } from "../../Common/Imports";
import { useAxios } from "../../Helpers/AxiosHelper";
import { ApiResponse } from "../../Models/Classes";

export interface ResetPasswordModel {
    Email: string,
    Token: string,
    Password?: string,
    PasswordRepeat?: string
}

const ResetPassword: React.FC = () => {
    const AxiosHelper = useAxios();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const config = getSettings();
    const { control, handleSubmit, watch } = useForm<ResetPasswordModel>();
    const [successReset, setSuccessReset] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [resetError, setResetError] = useState<string | null>(null);

    const NavigateTo = (path: string) => {
        navigate(Paths[path].value);
    };

    const onSubmit: SubmitHandler<ResetPasswordModel> = async (data: ResetPasswordModel) => {
        setShowLoader(true);
        if (data) {
            data.Email = searchParams.get("Email") ?? "";
            data.Token = searchParams.get("Token") ?? "";
            try {
                await AxiosHelper.post<ApiResponse<boolean>>("Auth/ResetPassword", data);
                setSuccessReset(true);
                setResetError(null);
            } catch (error: unknown) {
                if (axios.isAxiosError(error)) {
                    console.log(error);
                } else {
                    console.log(error);
                }
                setResetError(t("Common.Errors.SendEmail"));
            } finally {
                setShowLoader(false);
            }
        } else {
            setShowLoader(false);
        }
    };

    const password = watch('Password');

    return (
        <div className="z-5 w-full lg:w-8 px-6 text-center mt-8" style={{ maxWidth: '400px' }}>
            <Card>
                <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                    <div className="w-full flex align-items-center justify-content-center">
                        <img src={config.settings.cdnUrl + Paths.Icon.value} alt="logo" className="w-6rem" />
                    </div>
                    <h1 className="text-4xl font-light mt-4 text-primary-500">
                        {t("Auth.ResetPassword.Header")}
                    </h1>
                    {
                        showLoader ? (
                            <>
                                <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                            </>
                        )
                            : <>
                                {!successReset ?
                                    <>
                                        {resetError &&
                                            <p className="text-danger">
                                                {resetError}
                                            </p>
                                        }
                                        <p>
                                            {t("Auth.ResetPassword.SubHeader1")} {searchParams.get("Email")}
                                        </p>
                                        <p>
                                            {t("Auth.ResetPassword.SubHeader2")}
                                        </p>
                                        <div className="row mb-4 mt-5">
                                            <div className="col-md-12">
                                                <PasswordField<ResetPasswordModel>
                                                    control={control}
                                                    name="Password"
                                                    label={t("Auth.ResetPassword.Password")}                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <PasswordField<ResetPasswordModel>
                                                    control={control}
                                                    name="PasswordRepeat"
                                                    label={t("Auth.ResetPassword.RepeatPassword")}
                                                    validationRules={{
                                                        validate: (value) => value && value === password || t("Auth.Register.Form.User.PasswordRepeat") + " " + t("Auth.Register.Form.Mismatch"),
                                                        deps: ['PasswordRepeat'] as Path<ResetPasswordModel>[], // Correctly type deps
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col">
                                                <Button className="w-100" type="submit" label={t("Auth.ResetPassword.Button")} />
                                            </div>
                                        </div>
                                    </>
                                    :
                                    <div className="flex align-items-center justify-content-center mt-4 mb-5 gap-3 text-primary ">
                                        {t("Auth.ResetPassword.Success")}
                                    </div>
                                }
                                <div className="row">
                                    <div className="col">
                                        <a onClick={() => NavigateTo('Login')} className="text-primary secondary-button cursor-pointer">
                                            {t("Auth.Register.Link")}
                                        </a>
                                    </div>
                                </div>
                            </>
                    }

                </form>
            </Card>
        </div>
    )
};

export default ResetPassword;
